import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { IoLocationSharp } from 'react-icons/io5'
import { FaBed, FaBath } from 'react-icons/fa'
import { SlSizeFullscreen } from 'react-icons/sl'
import { FaSchool } from 'react-icons/fa6'
import axios from 'axios'
import { formatNumberComma, formatNumber } from '../utils/numberUtils'

const defaultCarImage = '/wp-content/uploads/2024/09/house-placeholder.jpg'

const dollarsToNumber = (v) => {
  if (v == null) return null
  if (typeof v === 'number') return v
  if (typeof v === 'string') {
    const n = Number(v.replace(/[^0-9.]/g, ''))
    return Number.isFinite(n) ? n : null
  }
  return null
}

const extractFromEssentialInfo = (list = []) => {
  const out = { propertyType: null, sleeps: null, bedrooms: null, bathrooms: null, beds: null }
  list.forEach((sRaw) => {
    const s = String(sRaw || '').trim()
    if (!out.propertyType && /^(entire|private)\b/i.test(s)) out.propertyType = s
    const n = (re) => {
      const m = re.exec(s)
      return m ? Number(m[1]) : null
    }
    const vSleeps = n(/(\d+)\s*sleeps?/i)
    const vBedrooms = n(/(\d+)\s*bedrooms?/i)
    const vBathrooms = n(/(\d+)\s*bath(?:rooms?)?/i)
    const vBeds = n(/(\d+)\s*beds?/i)
    if (vSleeps != null && out.sleeps == null) out.sleeps = vSleeps
    if (vBedrooms != null && out.bedrooms == null) out.bedrooms = vBedrooms
    if (vBathrooms != null && out.bathrooms == null) out.bathrooms = vBathrooms
    if (vBeds != null && out.beds == null) out.beds = vBeds
  })
  return out
}

const getNights = (ci, co) => {
  if (!ci || !co) return null
  const start = new Date(ci)
  const end = new Date(co)
  const ms = end - start
  if (Number.isNaN(ms) || ms <= 0) return null
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

// Build an external Google Hotels URL when possible
const buildSerpUrl = (p) => {
  const token =
    p?.property_token ||
    p?.propertyId ||
    p?.property_id ||
    p?.hotel_id ||
    null
  if (token) {
    return `https://www.google.com/travel/hotels/entity/${encodeURIComponent(token)}`
  }
  if (p?.link && /^https?:\/\//i.test(p.link)) return p.link
  if (p?.serpapi_property_details_link && /^https?:\/\//i.test(p.serpapi_property_details_link)) {
    return p.serpapi_property_details_link
  }
  return null
}

const mapSerpProperty = (p, nights) => {
  const title = p?.title || p?.name || p?.description || p?.snippet || p?.summary || ''
  const formattedAddress =
    p?.address?.full ||
    p?.address?.street_address ||
    p?.neighborhood ||
    p?.area ||
    p?.location ||
    ''

  const imageFromArray =
    p?.images?.[0]?.thumbnail ||
    p?.images?.[0]?.image ||
    p?.images?.[0]?.original_image ||
    (Array.isArray(p?.images) ? p.images[0] : null)
  const thumbnail = p?.thumbnail || imageFromArray || null

  const rating = p?.overall_rating ?? p?.rating ?? p?.review_rating ?? null
  const amenitiesArr = Array.isArray(p?.amenities)
    ? p.amenities.map(a => (typeof a === 'string' ? a : a?.name || '')).filter(Boolean)
    : []

  const perNight =
    p?.rate_per_night?.extracted_lowest ??
    p?.rate_per_night?.extracted_before_taxes_fees ??
    p?.price_per_night?.extracted_lowest ??
    p?.price_per_night?.extracted_before_taxes_fees ??
    dollarsToNumber(p?.rate_per_night?.lowest) ??
    dollarsToNumber(p?.price_per_night?.lowest) ??
    null

  const total =
    p?.total_rate?.extracted_lowest ??
    p?.total_rate?.extracted_before_taxes_fees ??
    p?.total_price?.value ??
    dollarsToNumber(p?.total_rate?.lowest) ??
    (perNight && nights ? perNight * nights : null)

  const ess = extractFromEssentialInfo(p?.essential_info)

  const id =
    p?.property_token ||
    p?.property_id ||
    p?.hotel_id ||
    p?.link ||
    p?.serpapi_property_details_link ||
    String(Math.random())

  return {
    __source: 'serp',
    id,
    formattedAddress,
    featured_image_url: thumbnail || undefined,
    imageUrls: thumbnail ? [thumbnail] : undefined,
    title: { rendered: title },
    rating,
    amenities: amenitiesArr,
    propertyType: ess.propertyType,
    sleeps: ess.sleeps,
    bedrooms: ess.bedrooms,
    bathrooms: ess.bathrooms,
    beds: ess.beds,
    meta: {
      hp_price: perNight ?? null,
      hp_h_fs_beds: ess.bedrooms ?? ess.beds ?? null,
      hp_h_fs_baths: ess.bathrooms ?? null,
      hp_location: formattedAddress || null,
      hp_sleeps: ess.sleeps ?? null,
    },
    perNight,
    totalStay: total,
    externalUrl: buildSerpUrl(p), // <- added
  }
}

const firstNum = (...vals) => {
  for (const v of vals) {
    const n = Number(v)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

const HouseGrid = ({ data = [], type = '' }) => {
  const [images, setImages] = useState({})
  const [errors, setErrors] = useState({})

  const { checkin_date, checkout_date } = useSelector(
    s => s.shortTermHousingRentals?.filters || {}
  )
  const nights = getNights(checkin_date, checkout_date)

  useEffect(() => {
    const fetchStreetViewImage = async (address, id) => {
      try {
        const url = 'https://maps.googleapis.com/maps/api/streetview'
        const response = await axios.get(url, {
          params: { size: '600x300', location: address, key: process.env.REACT_APP_GOOGLE_API },
          responseType: 'blob',
        })
        const imageBlob = URL.createObjectURL(response.data)
        setImages(prev => ({ ...prev, [id]: imageBlob }))
      } catch (error) {
        console.error('Error fetching the Street View image:', error)
        setErrors(prev => ({ ...prev, [id]: 'Failed to fetch Street View.' }))
        setImages(prev => ({ ...prev, [id]: defaultCarImage }))
      }
    }

    data.forEach(item => {
      const address =
        item?.formattedAddress || item?.address?.address || item?.meta?.hp_location
      const hasImage =
        item?.featured_image_url || item?.imageUrls?.[0] || item?.thumbnail
      if (!hasImage && address) {
        const id =
          item?.id || item?.property_id || item?.property_token || String(Math.random())
        fetchStreetViewImage(address, id)
      }
    })
  }, [data])

  return (
    <div className='house-grid'>
      {data.length > 0
        ? data.map((raw, index) => {
            const looksSerp =
              raw?.properties_source === 'serp' ||
              raw?.rating !== undefined ||
              raw?.amenities !== undefined ||
              raw?.price_per_night !== undefined ||
              raw?.rate_per_night !== undefined ||
              raw?.total_price !== undefined

            const car = looksSerp ? mapSerpProperty(raw, nights) : raw
            const isSerpCard = looksSerp || car?.__source === 'serp'

            const address =
              car?.formattedAddress || car?.address?.address || car?.meta?.hp_location || ''

            const fallbackStreetView =
              'https://maps.googleapis.com/maps/api/streetview?size=600x300&location=' +
              encodeURIComponent(address || '') +
              `&key=${process.env.REACT_APP_GOOGLE_API}`

            const imageSrc =
              car?.featured_image_url ||
              car?.imageUrls?.[0] ||
              car?.thumbnail ||
              images[car?.id] ||
              fallbackStreetView ||
              defaultCarImage

            const showBeds = firstNum(car?.beds, car?.meta?.hp_h_fs_beds, car?.bedrooms)
            const showBaths = firstNum(car?.bathrooms, car?.meta?.hp_h_fs_baths)

            const idKey = car?.id ?? index
            const href = `/details/?detail_id=${car?.id}${
              String(car?.id || '').length > 5 && type !== '' ? `&type=${type}` : ''
            }`

            const serpUrl = isSerpCard ? (car?.externalUrl || buildSerpUrl(raw)) : null

            const ImageBlock = (
              <div>
                <img
                  src={imageSrc}
                  alt='House'
                  className='car-image'
                  onError={e => {
                    if (e.target.src !== defaultCarImage) e.target.src = defaultCarImage
                  }}
                  loading='lazy'
                />
              </div>
            )

            return (
              <div key={idKey} className='house-card'>
                {isSerpCard ? (
                  serpUrl ? (
                    <a href={serpUrl} target="_blank" rel="noopener noreferrer">
                      {ImageBlock}
                    </a>
                  ) : (
                    ImageBlock
                  )
                ) : (
                  <a href={href}>{ImageBlock}</a>
                )}

                <div className='content'>
                  <h3>
                    {isSerpCard ? (
                      serpUrl ? (
                        <a
                          href={serpUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          dangerouslySetInnerHTML={{
                            __html: car?.title?.rendered || car?.formattedAddress || 'Listing',
                          }}
                        />
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: car?.title?.rendered || car?.formattedAddress || 'Listing',
                          }}
                        />
                      )
                    ) : (
                      <a
                        href={href}
                        dangerouslySetInnerHTML={{
                          __html: car?.title?.rendered || car?.formattedAddress || 'Listing',
                        }}
                      />
                    )}
                  </h3>

                  {address && (
                    <div className='car-attr'>
                      <span>
                        <IoLocationSharp size={18} /> {address}
                      </span>
                    </div>
                  )}

                  {(showBeds || showBaths || car?.meta?.hp_size_sq_ft || car?.squareFootage || car?.meta?.hp_school_district || car?.sleeps || car?.propertyType) && (
                    <div className='car-attr-wrap'>
                      {showBeds && (
                        <div className='car-attr'>
                          <span>
                            <FaBed size={18} /> {showBeds} beds
                          </span>
                        </div>
                      )}
                      {showBaths && (
                        <div className='car-attr'>
                          <span>
                            <FaBath size={16} /> {showBaths} baths
                          </span>
                        </div>
                      )}
                      {Number(car?.meta?.hp_size_sq_ft) > 0 && (
                        <div className='car-attr'>
                          <span>
                            <SlSizeFullscreen size={12} /> {formatNumberComma(car.meta.hp_size_sq_ft)} sq.ft
                          </span>
                        </div>
                      )}
                      {Number(car?.squareFootage) > 0 && (
                        <div className='car-attr'>
                          <span>
                            <SlSizeFullscreen size={12} /> {formatNumberComma(car.squareFootage)} sq.ft
                          </span>
                        </div>
                      )}
                      {car?.meta?.hp_school_district && (
                        <div className='car-attr'>
                          <span>
                            <FaSchool size={18} /> {car?.meta?.hp_school_district}
                          </span>
                        </div>
                      )}
                      {car?.propertyType && (
                        <div className='car-attr'>
                          <span>{car.propertyType}</span>
                        </div>
                      )}
                      {Number.isFinite(Number(car?.sleeps)) && (
                        <div className='car-attr'>
                          <span>Sleeps {car.sleeps}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {Number.isFinite(Number(car?.meta?.hp_price)) ? (
                    <span className='car-attr price-tag'>
                      ${formatNumber(car.meta.hp_price)}
                    </span>
                  ) : Number.isFinite(Number(car?.price)) ? (
                    <span className='car-attr price-tag'>
                      ${formatNumber(car.price)}
                    </span>
                  ) : (
                    <span className='car-attr price-tag'>Contact seller for the price</span>
                  )}

                  {(Number.isFinite(Number(car?.perNight)) || Number.isFinite(Number(car?.totalStay))) && (
                    <div className='car-attr serp-extra'>
                      {Number.isFinite(Number(car?.perNight)) && (
                        <div><strong>Per Night:</strong> ${formatNumber(car.perNight)}</div>
                      )}
                      {Number.isFinite(Number(car?.totalStay)) && (
                        <div><strong>Total Stay:</strong> ${formatNumber(car.totalStay)}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        : null}
      {Object.values(errors).length > 0 && <p>Some images failed to load.</p>}
    </div>
  )
}

export default HouseGrid
